// The London skyline watermark on the DARK app surfaces: the Client Portal footer
// (routes/portal/PortalShell.tsx) and the engineer PWA home screen
// (routes/engineer/EngineerHome.tsx). One source, because this treatment has now been adjusted
// three times and the two surfaces drifted apart each time -- Session AFF used a filter chain,
// Session AFG replaced it with an exact-colour CSS mask on the engineer home ONLY, and Session
// AFJ then found the portal still rendering the raw artwork at Session ACG's 0.55.
//
// WHY A MASK AND NOT MORE OPACITY. The artwork (public/brand/london-skyline*.png) is thin
// anti-aliased line work in a near-black purple -- measured dominant stroke #3a0089, and NOT one
// fully opaque pixel in the file (peak alpha 212/255, p90 113/255, median 39/255), so the
// effective opacity is always far below the nominal figure. Composited over the #050816 app
// background at the portal's old 0.55, the BRIGHTEST stroke lands on #1d044b: a contrast ratio of
// 1.11 against the background, which is to say invisible. Raising the opacity only converges on
// the artwork's own near-black colour, so no opacity value could ever have fixed it. A CSS mask
// instead uses the artwork's ALPHA as a stencil and fills the element with a colour of our
// choosing, so the rendered colour is exact by construction rather than a filter's approximation
// of a hue.
//
// THE VALUES (Director-set, Session AFJ). #e8f4fd at 0.35. Measured and eyeballed against 0.28,
// 0.45 and #ffffff at 0.25 before settling: the Shard, the Gherkin and Tower Bridge all read as a
// London skyline, while the strip stays recessive enough to be a watermark rather than a feature
// competing with the content above it. 0.45 was legible but began to pull the eye.
//
// MEASURE AT THE PAINTED SIZE, NOT THE SOURCE SIZE. The strip renders 240x80 (see the height note
// below), a 5x downscale of the 1200x400 artwork, and area-averaging thin anti-aliased lines
// against their transparent neighbours roughly HALVES the peak alpha on the way down: 212/255 at
// 1:1 becomes 113/255 as painted. So the brightest stroke lands on #282d3a (contrast 1.44), not
// the #474d59 / 2.34 the source-resolution figure suggests. Judge any future adjustment on a
// composite rendered at 240x80, or it will read as brighter than it ships.
// The 1.11 -> 1.44 gain also understates the improvement, because the ratio cannot see hue: the
// old purple and AFG's blue both sat INSIDE the background's own blue-black hue family, whereas a
// neutral near-white separates from it at a far lower ratio than a coloured stroke would need.
//
// These are the DARK-surface values, used by the Client Portal footer, the engineer PWA home, and
// (Session AFW, Director-directed, superseding this note's earlier "deliberately different"
// wording) the public project quotation page footer -- all dark #050816 grounds. No surface
// renders the raw artwork on a dark ground any more.
// NB the customer quote accept page took these values in Session AGZ, when that page was dark, and
// took the LIGHT pair below when Session AHD reverted it. That is the whole point of the split: the
// pair follows the GROUND the strip is painted on, never the page it belongs to.
export const SKYLINE_WATERMARK_COLOUR = "#e8f4fd";
export const SKYLINE_WATERMARK_OPACITY = 0.35;

// ---- LIGHT GROUNDS (Session AFK; also the customer accept page since Session AHD) --------------
// The engineer PWA home screen is themable (.engineer-ui) and the customer quote accept page is
// light outright, so on either the near-white fill above disappears entirely. (AHD is the proof
// that this pair earns its keep: reverting that page's canvas to light without switching the pair
// would have shipped an invisible strip -- the AFJ defect, mirrored.)
// The mirror of the dark treatment is a dark ink on the light
// ground, and it is measured the same way rather than guessed: the AFK brief proposed #050816 at
// 0.08, which the method below shows lands an effective alpha of 0.038 and a contrast of 1.08.
// That is invisible -- the exact defect Session AFJ had just fixed in the dark direction, and the
// reason this file insists on measuring at the painted size.
//
// MEASURED, composite of the real public/brand/london-skyline.png over #f8f9fa at the painted
// 240x80, quoted at both the peak stroke and the p90 body stroke so the value is not tuned to a
// handful of pixels (28.5% of the strip carries any ink at all; peak alpha 121/255, p90 51/255):
//     opacity   peak stroke        body stroke
//     0.08      #eff0f1  1.08      -            invisible, rejected
//     0.25      #dbdcdf  1.30      #ecedef 1.11
//     0.30      #d5d7d9  1.37      #eaebec 1.14
//     0.35      #d0d1d4  1.45      #e7e8ea 1.16   <-- chosen
//     0.40      #cacbcf  1.54      #e5e6e8 1.19
//     0.45      #c4c5c9  1.63      #e2e3e6 1.21
// The SAME method run over the shipped dark pair (#e8f4fd at 0.35 over #050816, same asset, same
// painted size) gives peak 1.50 / body 1.13, so 0.35 is the closest match on BOTH figures: the two
// themes carry the same weight of watermark, which is the point. NB that dark control reads 1.50
// here against the 1.44 recorded above because the two runs measured different files -- the
// engineer home masks the 2172x724 master while the AFJ figure came off the 1200px derivative.
// Like-for-like is what matters, so both figures in this block come off the master.
export const SKYLINE_WATERMARK_COLOUR_LIGHT = "#050816";
// Session AJW: doubled 0.35 -> 0.70, Director decision, confirmed against the actual live
// computed colour (rgb(41, 16, 75) / #29104b on AcceptQuote.tsx and RenewalAccept.tsx -- the
// page-scoped purple ink from AcceptQuote.css's --cq-skyline-ink, already correct per Session
// AHR above; this was never a colour bug, only an opacity one). The measured contrast figures
// in the table above were taken at 0.35; doubling the opacity roughly doubles the effective
// alpha, making the watermark noticeably more visible without changing which colour it renders.
export const SKYLINE_WATERMARK_OPACITY_LIGHT = 0.7;

// Both surfaces render the strip at a fixed 80px. The height MUST be explicit: a mask fills an
// element's background, and an empty <div> has no intrinsic height, so a max-height alone would
// render it 0px tall (invisible -- the exact fault this treatment exists to fix).
export const SKYLINE_WATERMARK_HEIGHT = "80px";
